import React, { useState, useEffect, useContext } from "react";
import { XCard } from "../..";
import { DataTable } from "./DataTable";
import ReusableTabs from "../../ReusableTabs";
import {
  educationColumns,
  experienceColumns,
  statusColumns,
  languageColumns,
  salaryColumns,
  emailColumns,
  phoneColumns,
  addressColumns,
} from "./columns";
import {
  WorkIcon,
  SchoolIcon,
  ShineIcon,
  TranslateIcon,
  ClipboardIcon,
  CashIcon,
  Phone2Icon,
} from "../../../shared/images/icons";
import {
  SxProps,
  Theme,
  CircularProgress,
  Typography,
  Chip,
  Box,
  Button,
  Menu,
  MenuItem,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import {
  useCandidateData,
  EducationInfo,
  CandidateDataSection,
} from "../../../hooks/candidate/useCandidateData";
import { sortWorkExperiencesByDate } from "../../../shared/utils/recordDateSort";
import colors from "../../../shared/constants/color";
import {
  CommunicationParameterGuids,
  isCorporateCommunicationParameterId,
} from "../../../constants/communicationParameterGuids";
import AddEducationModal, {
  EducationInfoListDTO,
} from "../../../pages/candidateDetail/components/generalTab/AddEducationModal";
import AddWorkExperienceModal from "../../../pages/candidateDetail/components/generalTab/AddWorkExperienceModal";
import AddLanguageModal from "../../../pages/candidateDetail/components/generalTab/AddLanguageModal";
import AddSalaryExpectationModal from "../../../pages/candidateDetail/components/generalTab/AddSalaryExpectationModal";
import AddEmailModal, {
  EmailInfoListDTO,
} from "../../../pages/candidateDetail/components/generalTab/AddEmailModal";
import AddPhoneModal, {
  PhoneInfoListDTO,
} from "../../../pages/candidateDetail/components/generalTab/AddPhoneModal";
import AddAddressModal, {
  AddressInfoListDTO,
} from "../../../pages/candidateDetail/components/generalTab/AddAddressModal";
import { deleteEducationInfo } from "../../../api/educationApi";
import { deleteWorkExperience } from "../../../api/workExperienceApi";
import { deleteLanguageInfo } from "../../../api/languageApi";
import { deleteEmailInfo } from "../../../api/emailApi";
import { deletePhoneInfo } from "../../../api/phoneApi";
import { deleteAddressInfo } from "../../../api/addressApi";
import { deleteSalaryExpectation } from "../../../api/salaryExpectationApi";
import { useToastContext } from "../../../context/ToastContextProvider";
import { ConfirmDialog } from "./ConfirmDialog";
import AiSuggestedPositionsCard from "./AiSuggestedPositionsCard";
import { ThemeContext } from "../../../context/ThemeContextProvider";
import { getCardAddButtonSx } from "../../../shared/theme/wiserThemeHelpers";

type DeleteItemType =
  | "EDUCATION"
  | "WORK"
  | "LANGUAGE"
  | "EMAIL"
  | "PHONE"
  | "ADDRESS"
  | "SALARY_EXPECTATION";

type DeleteItem = {
  type: DeleteItemType;
  raw: any;
  id: string;
};

type DeleteConfig = {
  deleteFn: (id: string) => Promise<void>;
  successMessage: string;
  confirmTitle: string;
};

const DELETE_CONFIGS: Record<
  DeleteItemType,
  DeleteConfig & { section: CandidateDataSection }
> = {
  EDUCATION: {
    deleteFn: deleteEducationInfo,
    successMessage: "Eğitim bilgisi başarıyla silindi.",
    confirmTitle: "Eğitim Bilgisi Silinsin mi?",
    section: "education",
  },
  WORK: {
    deleteFn: deleteWorkExperience,
    successMessage: "İş tecrübesi başarıyla silindi.",
    confirmTitle: "İş Tecrübesi Silinsin mi?",
    section: "experience",
  },
  LANGUAGE: {
    deleteFn: deleteLanguageInfo,
    successMessage: "Dil bilgisi başarıyla silindi.",
    confirmTitle: "Dil Bilgisi Silinsin mi?",
    section: "languages",
  },
  EMAIL: {
    deleteFn: deleteEmailInfo,
    successMessage: "E-mail bilgisi başarıyla silindi.",
    confirmTitle: "E-mail Bilgisi Silinsin mi?",
    section: "emails",
  },
  PHONE: {
    deleteFn: deletePhoneInfo,
    successMessage: "Telefon bilgisi başarıyla silindi.",
    confirmTitle: "Telefon Bilgisi Silinsin mi?",
    section: "phones",
  },
  ADDRESS: {
    deleteFn: deleteAddressInfo,
    successMessage: "Adres bilgisi başarıyla silindi.",
    confirmTitle: "Adres Bilgisi Silinsin mi?",
    section: "addresses",
  },
  SALARY_EXPECTATION: {
    deleteFn: deleteSalaryExpectation,
    successMessage: "Maaş beklenti bilgisi başarıyla silindi.",
    confirmTitle: "Maaş Beklenti Bilgisi Silinsin mi?",
    section: "salary",
  },
};

interface IProps {
  sx?: SxProps<Theme>;
  memberId?: string;
  showAddButton?: boolean;
  editable?: boolean;
  talentId?: string;
  showAiPositions?: boolean;
  refetchCandidateDetail?: () => void;
}

export const CandidateDetailsCard: React.FC<IProps> = ({
  sx,
  memberId = "",
  showAddButton = false,
  editable = false,
  talentId,
  showAiPositions = false,
  refetchCandidateDetail,
}) => {
  const { showToast } = useToastContext();
  const { darkModeActive } = useContext(ThemeContext);
  const { data, loading, refetchSection } = useCandidateData(memberId);

  // Mutasyon sonrasi TUM veriyi degil yalnizca ilgili bolumu yeniler; loading
  // tetiklenmedigi icin accordion durumu bozulmaz.
  const refreshSection = (section: CandidateDataSection) => {
    void refetchSection(section);
  };

  const [educationList, setEducationList] = useState<
    (EducationInfo | EducationInfoListDTO)[]
  >(data?.educationList || []);
  const [experienceList, setExperienceList] = useState<any[]>(
    sortWorkExperiencesByDate(data?.experienceList || []),
  );
  const [languageList, setLanguageList] = useState<any[]>(
    data?.languages || [],
  );
  const [emailList, setEmailList] = useState<any[]>(
    data?.contact?.emails || [],
  );
  const [phoneList, setPhoneList] = useState<any[]>(
    data?.contact?.phones || [],
  );
  const [addressList, setAddressList] = useState<any[]>(
    data?.contact?.addresses || [],
  );
  const [addEducationOpen, setAddEducationOpen] = useState(false);
  const [editEducationId, setEditEducationId] = useState<string | null>(null);
  const [addWorkExperienceOpen, setAddWorkExperienceOpen] = useState(false);
  const [editWorkExperienceId, setEditWorkExperienceId] = useState<
    string | null
  >(null);
  const [addLanguageOpen, setAddLanguageOpen] = useState(false);
  const [editLanguageId, setEditLanguageId] = useState<string | null>(null);
  const [addSalaryExpectationOpen, setAddSalaryExpectationOpen] =
    useState(false);
  const [editSalaryExpectationId, setEditSalaryExpectationId] = useState<
    string | null
  >(null);
  const [addEmailOpen, setAddEmailOpen] = useState(false);
  const [editEmailId, setEditEmailId] = useState<string | null>(null);
  const [addPhoneOpen, setAddPhoneOpen] = useState(false);
  const [editPhoneId, setEditPhoneId] = useState<string | null>(null);
  const [addAddressOpen, setAddAddressOpen] = useState(false);
  const [editAddressId, setEditAddressId] = useState<string | null>(null);
  const [salaryList, setSalaryList] = useState<any[]>(
    (data?.salaryExpectations || [])
      .slice()
      .sort(
        (a: any, b: any) =>
          new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime(),
      ),
  );
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<DeleteItem | null>(null);
  const [contactTabValue, setContactTabValue] = useState(0);
  const [contactMenuAnchor, setContactMenuAnchor] =
    useState<null | HTMLElement>(null);

  // Tab change handler
  const handleContactTabChange = (
    event: React.SyntheticEvent,
    newValue: number,
  ) => {
    setContactTabValue(newValue);
  };

  const handleContactMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setContactMenuAnchor(event.currentTarget);
  };

  const handleContactMenuClose = () => {
    setContactMenuAnchor(null);
  };
  //  Genel silme fonksiyonu
  const confirmDeleteGeneric = async () => {
    if (!itemToDelete) return;
    setDeleteConfirmOpen(false);

    const config = DELETE_CONFIGS[itemToDelete.type];

    try {
      await config.deleteFn(itemToDelete.id);
      showToast(config.successMessage, "success");
      refreshSection(config.section);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        "Silme işlemi sırasında bir hata oluştu.";
      showToast(message, "error");
    } finally {
      setItemToDelete(null);
    }
  };

  const handleEducationEdit = (row: any) => {
    const educationId = row.educationID || row.educationId || row.id;
    setEditEducationId(educationId ? String(educationId) : null);
    setAddEducationOpen(true);
  };

  const handleEducationModalClose = () => {
    setAddEducationOpen(false);
    setEditEducationId(null);
  };
  const handleEducationDelete = (row: any) => {
    const educationId = row.educationID || row.educationId || row.id;

    if (!educationId) return;

    setItemToDelete({
      type: "EDUCATION",
      raw: row,
      id: String(educationId),
    });
    setDeleteConfirmOpen(true);
  };

  const handleWorkExperienceEdit = (row: any) => {
    console.log("Edit work experience clicked - row data:", row);
    const workExpId =
      row.workExperienceID || row.workExperienceId || row.id || row.ID;
    console.log("Work Experience ID to edit:", workExpId);
    setEditWorkExperienceId(workExpId ? String(workExpId) : null);
    setAddWorkExperienceOpen(true);
  };

  const handleWorkExperienceModalClose = () => {
    setAddWorkExperienceOpen(false);
    setEditWorkExperienceId(null);
  };

  const handleWorkExperienceDelete = (row: any) => {
    const workExperienceId =
      row.workExperienceID || row.workExperienceId || row.id;
    if (!workExperienceId) return;

    setItemToDelete({
      type: "WORK",
      raw: row,
      id: String(workExperienceId),
    });
    setDeleteConfirmOpen(true);
  };

  const handleLanguageEdit = (row: any) => {
    const languageId = row.languageID || row.languageId || row.id;
    setEditLanguageId(languageId ? String(languageId) : null);
    setAddLanguageOpen(true);
  };

  const handleLanguageModalClose = () => {
    setAddLanguageOpen(false);
    setEditLanguageId(null);
  };

  const handleLanguageDelete = (row: any) => {
    const languageId = row.languageID || row.languageId || row.id;

    if (!languageId) return;

    setItemToDelete({
      type: "LANGUAGE",
      raw: row,
      id: String(languageId),
    });
    setDeleteConfirmOpen(true);
  };

  const handleEmailEdit = (row: any) => {
    console.log("Edit email clicked - row data:", row);
    const emailId = row.emailID || row.emailId || row.id || row.ID;
    console.log("Email ID to edit:", emailId);
    setEditEmailId(emailId ? String(emailId) : null);
    setAddEmailOpen(true);
  };

  const handleEmailModalClose = () => {
    setAddEmailOpen(false);
    setEditEmailId(null);
  };

  const handleEmailDelete = (row: any) => {
    const emailId = row.emailID || row.emailId || row.id || row.ID;
    if (!emailId) return;

    setItemToDelete({
      type: "EMAIL",
      raw: row,
      id: String(emailId),
    });
    setDeleteConfirmOpen(true);
  };

  const handlePhoneEdit = (row: any) => {
    console.log("Edit phone clicked - row data:", row);
    const phoneId = row.phoneID || row.phoneId || row.id || row.ID;
    console.log("Phone ID to edit:", phoneId);
    setEditPhoneId(phoneId ? String(phoneId) : null);
    setAddPhoneOpen(true);
  };

  const handlePhoneModalClose = () => {
    setAddPhoneOpen(false);
    setEditPhoneId(null);
  };

  const handlePhoneDelete = (row: any) => {
    const phoneId = row.phoneID || row.phoneId || row.id || row.ID;
    if (!phoneId) return;

    setItemToDelete({
      type: "PHONE",
      raw: row,
      id: String(phoneId),
    });
    setDeleteConfirmOpen(true);
  };

  const handleAddressEdit = (row: any) => {
    console.log("Edit address clicked - row data:", row);
    const addressId = row.addressID || row.addressId || row.id || row.ID;
    console.log("Address ID to edit:", addressId);
    setEditAddressId(addressId ? String(addressId) : null);
    setAddAddressOpen(true);
  };

  const handleAddressModalClose = () => {
    setAddAddressOpen(false);
    setEditAddressId(null);
  };

  const handleAddressDelete = (row: any) => {
    const addressId = row.addressID || row.addressId || row.id || row.ID;
    if (!addressId) return;

    setItemToDelete({
      type: "ADDRESS",
      raw: row,
      id: String(addressId),
    });
    setDeleteConfirmOpen(true);
  };

  const handleSalaryExpectationEdit = (row: any) => {
    console.log("Edit salary expectation clicked - row data:", row);
    const salaryId = row.id || row.ID;
    console.log("Salary ID to edit:", salaryId);
    setEditSalaryExpectationId(salaryId ? String(salaryId) : null);
    setAddSalaryExpectationOpen(true);
  };

  const handleSalaryExpectationModalClose = () => {
    setAddSalaryExpectationOpen(false);
    setEditSalaryExpectationId(null);
  };

  const handleSalaryExpectationDelete = (row: any) => {
    const salaryExpectationId = row.id || row.ID;
    if (!salaryExpectationId) return;

    setItemToDelete({
      type: "SALARY_EXPECTATION",
      raw: row,
      id: String(salaryExpectationId),
    });
    setDeleteConfirmOpen(true);
  };

  useEffect(() => {
    if (contactMenuAnchor) {
      const handleScroll = () => {
        handleContactMenuClose();
      };

      const options = { capture: true, passive: false };
      window.addEventListener("scroll", handleScroll, options);
      document.addEventListener("scroll", handleScroll, options);

      return () => {
        window.removeEventListener("scroll", handleScroll, options);
        document.removeEventListener("scroll", handleScroll, options);
      };
    }
  }, [contactMenuAnchor]);

  const handleContactMenuItemClick = (
    type: "email" | "phone" | "address",
    event: React.MouseEvent<HTMLElement>,
  ) => {
    event.stopPropagation();
    handleContactMenuClose();

    if (type === "email") {
      setAddEmailOpen(true);
    }
    if (type === "phone") {
      setAddPhoneOpen(true);
    }
    if (type === "address") {
      setAddAddressOpen(true);
    }
  };

  // Tab içerikleri
  const contactTabs = [
    {
      label: `Email (${emailList?.length || 0})`,
      content: (
        <DataTable
          columns={emailColumns}
          data={emailList || []}
          emptyMessage="Email bilgisi bulunamadı"
          editable={editable}
          onEditClick={handleEmailEdit}
          onDeleteClick={handleEmailDelete}
        />
      ),
    },
    {
      label: `Telefon (${phoneList?.length || 0})`,
      content: (
        <DataTable
          columns={phoneColumns}
          data={phoneList || []}
          emptyMessage="Telefon bilgisi bulunamadı"
          editable={editable}
          onEditClick={handlePhoneEdit}
          onDeleteClick={handlePhoneDelete}
        />
      ),
    },
    {
      label: `Adres (${addressList?.length || 0})`,
      content: (
        <DataTable
          columns={addressColumns}
          data={addressList || []}
          emptyMessage="Adres bilgisi bulunamadı"
          editable={editable}
          onEditClick={handleAddressEdit}
          onDeleteClick={handleAddressDelete}
        />
      ),
    },
  ];

  React.useEffect(() => {
    if (data?.educationList) {
      setEducationList(data.educationList as any);
    }
  }, [data?.educationList]);

  React.useEffect(() => {
    if (data?.experienceList) {
      setExperienceList(
        sortWorkExperiencesByDate(data.experienceList as any[]),
      );
    }
  }, [data?.experienceList]);

  React.useEffect(() => {
    if (data?.languages) {
      setLanguageList(data.languages as any);
    }
  }, [data?.languages]);

  React.useEffect(() => {
    if (data?.salaryExpectations) {
      const sorted = (data.salaryExpectations as any[])
        .slice()
        .sort(
          (a, b) =>
            new Date(b.createdDate).getTime() -
            new Date(a.createdDate).getTime(),
        );
      setSalaryList(sorted);
    }
  }, [data?.salaryExpectations]);

  const sortByCommunicationType = <
    T extends { communicationType?: string; communicationParameterId?: string | number },
  >(
    items: T[],
    secondarySortKey: keyof T,
  ): T[] => {
    const typeOrder: Record<string, number> = {
      corporate: 1,
      [CommunicationParameterGuids.rdc]: 1,
      [CommunicationParameterGuids.work]: 1,
      İŞ: 2,
      ÖZEL: 3,
    };

    const resolveOrder = (item: T): number => {
      if (isCorporateCommunicationParameterId(item.communicationParameterId)) {
        return 1;
      }
      const type = item.communicationType || "";
      // Legacy seed display name until CommunicationParameter is renamed
      if (type === "RDC" || type.toLowerCase() === "corporate") {
        return 1;
      }
      return typeOrder[type] || 999;
    };

    return [...items].sort((a, b) => {
      const orderA = resolveOrder(a);
      const orderB = resolveOrder(b);

      if (orderA !== orderB) {
        return orderA - orderB;
      }

      const valueA = String(a[secondarySortKey] || "");
      const valueB = String(b[secondarySortKey] || "");
      return valueA.localeCompare(valueB, "tr");
    });
  };

  React.useEffect(() => {
    if (data?.contact?.emails) {
      const sorted = sortByCommunicationType(data.contact.emails, "emailName");
      setEmailList(sorted);
    }
  }, [data?.contact?.emails]);

  React.useEffect(() => {
    if (data?.contact?.phones) {
      const sorted = sortByCommunicationType(data.contact.phones, "phoneNo");
      setPhoneList(sorted);
    }
  }, [data?.contact?.phones]);

  React.useEffect(() => {
    if (data?.contact?.addresses) {
      const sorted = sortByCommunicationType(
        data.contact.addresses,
        "fullAddress",
      );
      setAddressList(sorted);
    }
  }, [data?.contact?.addresses]);

  if (loading && !data) {
    return (
      <XCard>
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      </XCard>
    );
  }

  return (
    <XCard
      sx={{
        ...sx,
        fontFamily:
          "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        "& *": {
          fontFamily:
            "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important",
        },
      }}
    >
      {showAiPositions && (
        <AiSuggestedPositionsCard talentId={talentId} memberId={memberId} />
      )}
      <XCard
        title="Eğitim"
        icon={<img src={SchoolIcon} />}
        style="white"
        expandable={true}
        defaultExpanded={true}
        headerAction={
          showAddButton ? (
            <Button
              size="small"
              startIcon={<AddIcon fontSize="small" />}
              sx={getCardAddButtonSx(darkModeActive)}
              onClick={(e) => {
                e.stopPropagation();
                setAddEducationOpen(true);
              }}
            >
              Yeni Ekle
            </Button>
          ) : undefined
        }
      >
        <DataTable
          columns={educationColumns}
          data={educationList || []}
          emptyMessage="Eğitim bilgisi bulunamadı"
          editable={editable}
          onEditClick={handleEducationEdit}
          onDeleteClick={handleEducationDelete}
        />
      </XCard>
      <XCard
        title="İş Tecrübesi"
        icon={<img src={WorkIcon} />}
        style="white"
        expandable={true}
        defaultExpanded={true}
        headerAction={
          showAddButton ? (
            <Button
              size="small"
              startIcon={
                <AddIcon fontSize="small" sx={{ color: colors.secondary }} />
              }
              sx={getCardAddButtonSx(darkModeActive)}
              onClick={(e) => {
                e.stopPropagation();
                setAddWorkExperienceOpen(true);
              }}
            >
              Yeni Ekle
            </Button>
          ) : undefined
        }
      >
        <DataTable
          columns={experienceColumns}
          data={experienceList || []}
          emptyMessage="İş tecrübesi bilgisi bulunamadı"
          editable={editable}
          onEditClick={handleWorkExperienceEdit}
          onDeleteClick={handleWorkExperienceDelete}
        />
      </XCard>
      {!editable && (
        <XCard
          title="Durum"
          icon={<img src={ClipboardIcon} />}
          style="white"
          expandable={true}
        >
          <DataTable
            columns={statusColumns}
            data={data?.status || []}
            emptyMessage="Durum bilgisi bulunamadı"
            editable={editable}
          />
        </XCard>
      )}
      {/* <XCard
        title="Yetkinlikler"
        icon={<img src={ShineIcon} />}
        style="white"
        expandable={true}
        headerAction={
          showAddButton ? (
            <Button
              size="small"
              startIcon={<AddIcon fontSize="small" />}
              sx={getCardAddButtonSx(darkModeActive)}
            >
              Yeni Ekle
            </Button>
          ) : undefined
        }
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {data?.skills?.length ? (
            data.skills.map((skill, index) => (
              <Chip
                key={index}
                label={
                  <span>
                    <strong style={{ fontWeight: 700 }}>
                      {skill.technicName}
                    </strong>
                    {(skill.levelName || skill.year) && " - "}
                    {skill.levelName || (skill.year ? `${skill.year} yıl` : "")}
                  </span>
                }
                size="medium"
                variant="outlined"
                sx={{
                  backgroundColor: colors.neutral.N1300,
                  borderColor: colors.blue.B1000,
                  // color: "#1E40AF",
                  fontWeight: 500,
                  fontSize: 12,
                  fontFamily:
                    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                }}
              />
            ))
          ) : (
            <Typography variant="body2">
              Yetkinlik bilgisi bulunamadı
            </Typography>
          )}
        </Box>
      </XCard> */}

      <XCard
        title="Yabancı Dil Bilgileri"
        icon={<img src={TranslateIcon} />}
        style="white"
        expandable={true}
        headerAction={
          showAddButton ? (
            <Button
              size="small"
              startIcon={<AddIcon fontSize="small" />}
              sx={getCardAddButtonSx(darkModeActive)}
              onClick={(e) => {
                e.stopPropagation();
                setAddLanguageOpen(true);
              }}
            >
              Yeni Ekle
            </Button>
          ) : undefined
        }
      >
        <DataTable
          columns={languageColumns}
          data={languageList || []}
          emptyMessage="Dil bilgisi bulunamadı"
          editable={editable}
          onEditClick={handleLanguageEdit}
          onDeleteClick={handleLanguageDelete}
        />
      </XCard>
      <XCard
        title="İletişim Bilgileri"
        icon={<img src={Phone2Icon} />}
        style="white"
        expandable={true}
        headerAction={
          showAddButton ? (
            <Box sx={{ position: "relative" }}>
              <Button
                size="small"
                startIcon={<AddIcon fontSize="small" />}
                sx={getCardAddButtonSx(darkModeActive)}
                onClick={handleContactMenuOpen}
              >
                Yeni Ekle
              </Button>
              <Menu
                anchorEl={contactMenuAnchor}
                open={Boolean(contactMenuAnchor)}
                onClose={(event, reason) => {
                  if (event && "stopPropagation" in event) {
                    (event as React.MouseEvent).stopPropagation();
                  }
                  handleContactMenuClose();
                }}
                onClick={(e) => e.stopPropagation()}
                disableScrollLock
                disablePortal
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "center",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "center",
                }}
                MenuListProps={{
                  onClick: (e) => e.stopPropagation(),
                  sx: {
                    padding: "4px 0",
                  },
                }}
                PaperProps={{
                  sx: {
                    minWidth: 70,
                    border: `0.75px solid ${colors.secondary}`,
                    borderTop: "none",
                    borderRadius: 1,
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 0,
                    boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
                  },
                }}
              >
                <MenuItem
                  onClick={(e) => handleContactMenuItemClick("email", e)}
                  sx={{
                    fontSize: "12px",
                    fontFamily: "Inter",
                    padding: "4px 16px",
                    minHeight: "auto",
                  }}
                >
                  Email
                </MenuItem>
                <MenuItem
                  onClick={(e) => handleContactMenuItemClick("phone", e)}
                  sx={{
                    fontSize: "12px",
                    fontFamily: "Inter",
                    padding: "4px 16px",
                    minHeight: "auto",
                  }}
                >
                  Telefon
                </MenuItem>
                <MenuItem
                  onClick={(e) => handleContactMenuItemClick("address", e)}
                  sx={{
                    fontSize: "12px",
                    fontFamily: "Inter",
                    padding: "4px 16px",
                    minHeight: "auto",
                  }}
                >
                  Adres
                </MenuItem>
              </Menu>
            </Box>
          ) : undefined
        }
      >
        <ReusableTabs
          value={contactTabValue}
          onChange={handleContactTabChange}
          tabs={contactTabs}
          tabSx={{
            textTransform: "none",
            fontSize: "14px",
            fontWeight: 600,
            minHeight: "48px",
          }}
          tabsSx={{
            "& .MuiTabs-indicator": {
              backgroundColor: colors.primary,
            },
          }}
        />
      </XCard>
      {memberId && (
        <AddEducationModal
          open={addEducationOpen}
          memberId={memberId}
          educationId={editEducationId}
          onClose={handleEducationModalClose}
          onSuccess={() => {
            refreshSection("education");
          }}
        />
      )}
      {memberId && (
        <AddWorkExperienceModal
          open={addWorkExperienceOpen}
          memberId={memberId}
          workExperienceId={editWorkExperienceId}
          onClose={handleWorkExperienceModalClose}
          onSuccess={() => {
            refreshSection("experience");
          }}
        />
      )}
      {memberId && (
        <AddLanguageModal
          open={addLanguageOpen}
          memberId={memberId}
          languageId={editLanguageId}
          onClose={handleLanguageModalClose}
          onSuccess={() => {
            refreshSection("languages");
          }}
        />
      )}
      <XCard
        title="Maaş Beklentisi"
        icon={<img src={CashIcon} />}
        style="white"
        expandable={true}
        headerAction={
          showAddButton ? (
            <Button
              size="small"
              startIcon={<AddIcon fontSize="small" />}
              sx={getCardAddButtonSx(darkModeActive)}
              onClick={(e) => {
                e.stopPropagation();
                setAddSalaryExpectationOpen(true);
              }}
            >
              Yeni Ekle
            </Button>
          ) : undefined
        }
      >
        <DataTable
          columns={salaryColumns}
          data={salaryList || []}
          emptyMessage="Maaş beklentisi bulunamadı"
          editable={editable}
          onEditClick={handleSalaryExpectationEdit}
          onDeleteClick={handleSalaryExpectationDelete}
        />
      </XCard>
      {memberId && (
        <AddSalaryExpectationModal
          open={addSalaryExpectationOpen}
          memberId={memberId}
          salaryExpectationId={editSalaryExpectationId}
          onClose={handleSalaryExpectationModalClose}
          onSuccess={() => {
            refreshSection("salary");
          }}
        />
      )}
      {memberId && (
        <AddEmailModal
          open={addEmailOpen}
          memberId={memberId}
          emailId={editEmailId}
          onClose={handleEmailModalClose}
          onSuccess={() => {
            refreshSection("emails");
          }}
        />
      )}
      {memberId && (
        <AddPhoneModal
          open={addPhoneOpen}
          memberId={memberId}
          phoneId={editPhoneId}
          onClose={handlePhoneModalClose}
          onSuccess={() => {
            refreshSection("phones");
          }}
        />
      )}
      {memberId && (
        <AddAddressModal
          open={addAddressOpen}
          memberId={memberId}
          addressId={editAddressId}
          onClose={handleAddressModalClose}
          onSuccess={() => {
            refreshSection("addresses");
          }}
        />
      )}
      <ConfirmDialog
        open={deleteConfirmOpen}
        title={
          itemToDelete ? DELETE_CONFIGS[itemToDelete.type].confirmTitle : ""
        }
        message="Bu kaydı silmek istediğinize emin misiniz?"
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={confirmDeleteGeneric}
      />
    </XCard>
  );
};
